import type { DeckProps } from '@deck.gl/core';
import { MapboxOverlay } from '@deck.gl/mapbox';
import type { Device } from '@luma.gl/core';
import WebMercatorViewport from '@math.gl/web-mercator';
import { type MouseEvent, memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  type MapRef,
  Map as ReactMap,
  type ViewState,
  type ViewStateChangeEvent,
  useControl,
} from 'react-map-gl/mapbox';
import type { RectReadOnly as MeasureResult } from 'react-use-measure';
import { type RenderInfoBuffer, getRenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import {
  FAST_ANIMATION_DURATION,
  SHADOW_TRACK_STEP,
  type Tuple2,
  type Tuple3,
} from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import {
  getCurrentEvents,
  getEventExternalLayers,
  getEventStarters,
  getHiddenIds,
  getOptions,
  getSelectedIds,
  getVisibleEventPois,
  getVisibleEventTracks,
  getVisibleSelectedPois,
  getVisibleSelectedStarters,
  getVisibleSelectedTracks,
} from 'src/lib/player/selectors';
import { denormaliseZoom, normaliseZoom } from 'src/lib/player/shareUrl';
import {
  type LatLngOpacity,
  type PoiType,
  type StarterType,
  type State,
  type TrackType,
  useStore,
} from 'src/lib/player/store';
import { easeInOut, getBoundsFromCoordinates } from 'src/lib/player/utils';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { IconThumbtack } from '../BasicComponents/Icons';
import { distanceMarkerLayerPrefix } from '../BasicComponents/Maps/Layers/DistanceMarkerLayer';
import { TrackLayer } from '../BasicComponents/Maps/Layers/TrackLayer';
import FullscreenControl from './Map/FullscreenControl';
import NavigationControl from './Map/NavigationControl';
import OwnLocationControl from './Map/OwnLocationControl';
import { createPOILayer } from './Map/POILayer';
import ScaleControl from './Map/ScaleControl';
import createStarterMarkerLayer from './Map/StarterMarkerLayer';
import createStarterParticleLayer from './Map/StarterParticleLayer';
import createStarterTrackLayer from './Map/StarterTrackLayer';
import createTrackHoverLayer from './Map/TrackHoverLayer';
import { useMapStyle } from './Map/loadMapStyle';
import {
  buildTrackLayerProps,
  focusedMarkerTrackId,
  markerLinesByTrack,
  needsReorder,
} from './Map/trackLayerProps';

type Viewport = ViewState & {
  width: number;
  height: number;
};

export type PointerEvent = MouseEvent & {
  center: {
    x: number;
    y: number;
  };
};

function translateViewport(
  viewport: Viewport,
  source: MeasureResult,
  target: MeasureResult,
): Viewport {
  const mercatorProjection = new WebMercatorViewport(viewport);

  const targetCenter = [target.left + target.width / 2, target.top + target.height / 2];
  const targetCenterRelativeToSource = [
    targetCenter[0] - source.left,
    targetCenter[1] - source.top,
  ];

  const centerLngLat = mercatorProjection.unproject(targetCenterRelativeToSource);

  return {
    ...viewport,
    longitude: centerLngLat[0],
    latitude: centerLngLat[1],
    width: target.width,
    height: target.height,
  };
}

function fitBounds(
  coordinates: Array<Tuple2 | Tuple3>,
  viewport: Viewport,
  padding = 0.2,
): Viewport {
  if (coordinates.length === 0) {
    return viewport;
  }
  const bounds = getBoundsFromCoordinates(coordinates);
  const { longitude, latitude, zoom } = new WebMercatorViewport(viewport).fitBounds(
    [
      [bounds.min_lng, bounds.min_lat],
      [bounds.max_lng, bounds.max_lat],
    ],
    {
      padding: {
        top: viewport.height * padding,
        bottom: viewport.height * padding,
        left: viewport.width * padding,
        right: viewport.width * padding,
      },
    },
  );
  return {
    ...viewport,
    latitude,
    longitude,
    zoom,
  };
}

/**
 * Edge band of the visible map. Re-framing and switching follow mode off must use the same value, or
 * a drag into the band keeps follow on and the map undoes the drag.
 */
const SELECTION_EDGE_PADDING = 0.1;

function areSelectedInView(
  selectedStarters: Array<StarterType>,
  selectedPois: Array<PoiType>,
  selectedTracks: Array<TrackType>,
  ownLocation: LatLngOpacity | null,
  visibleViewport: Viewport,
  renderInfoBuffer: RenderInfoBuffer,
): [boolean, Array<Tuple3>] {
  if (
    selectedStarters.length === 0 &&
    selectedPois.length === 0 &&
    selectedTracks.length === 0 &&
    ownLocation == null
  ) {
    return [false, []];
  }
  const proj = new WebMercatorViewport(visibleViewport);
  let selectedCoordinates: Array<Tuple3> = selectedStarters
    .map((starter) =>
      renderInfoBuffer.getLatLngOpacity(
        renderInfoBuffer.getRenderIndex(starter.eventIndex, starter.index),
      ),
    )
    .filter((latlng) => latlng.opacity > 0)
    .map((latlng) => [latlng.lng, latlng.lat, 0]);

  if (ownLocation != null) {
    selectedCoordinates.push([ownLocation.lng, ownLocation.lat, 0]);
  }

  for (const poi of selectedPois) {
    selectedCoordinates.push([poi.latlng.lng, poi.latlng.lat, 0]);
  }
  for (const track of selectedTracks) {
    selectedCoordinates = selectedCoordinates.concat(track.coordinates);
  }

  const padding = SELECTION_EDGE_PADDING;
  const allInView = selectedCoordinates.every((coordinate) => {
    const [x, y] = proj.project(coordinate);
    return (
      padding * proj.width < x &&
      x < (1 - padding) * proj.width &&
      padding * proj.height < y &&
      y < (1 - padding) * proj.height
    );
  });
  return [allInView, selectedCoordinates];
}

const timeSelector = (state: State) => state.timer.time;
const allEventsSelector = (state: State) => state.events;
const ownLocationSelector = (state: State) => state.timer.geolocation.latlng;
const highlightedIdSelector = (state: State) => state.timer.highlighted;
const highlightedLocationSelector = (state: State): Tuple2 | null => {
  const highlightedOffset = state.timer.highlightedOffset;
  if (highlightedOffset >= 0) {
    return (
      state.events[state.currentEventIndices[0]]?.chunkedShadowTrack?.getLngLatInterpolated(
        highlightedOffset / SHADOW_TRACK_STEP,
      ) ?? null
    );
  }
  return null;
};
const starterTrackInfoSelector = (state: State) =>
  state.currentEventIndices
    .filter((i) => state.eventsChangeable[i].starterTracks.size > 0)
    .map((i) => ({
      tracks: Array.from(state.eventsChangeable[i].starterTracks.entries()).filter(
        ([, points]) => points.length > 0,
      ),
      tailTimeout: state.events[i].options.tailTimeout,
      markerTimeout: state.events[i].options.markerTimeout,
    }));

function DeckGLOverlay(props: DeckProps) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

function MapComponent({
  visibleMapBounds,
  mapBounds,
}: {
  visibleMapBounds: MeasureResult;
  mapBounds: MeasureResult;
}) {
  const { width, height } = mapBounds;
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);
  const setHighlighted = useStore((state) => state.actions.setHighlighted);
  const setCurrentViewport = useStore((state) => state.actions.setCurrentViewport);

  const mapRef = useRef<MapRef>(null);
  const currentViewStateRef = useRef<ViewState>({
    latitude: 0,
    longitude: 0,
    zoom: 0,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });
  const interactionStateRef = useRef({
    isDragging: false,
    isZoomingRotating: false,
    isOurTransition: false,
  });

  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const [shouldFollow, setShouldFollow] = useState(
    () => useStore.getState().options.overrideViewport == null,
  );
  const deviceRef = useRef<Device>();

  const allEvents = useStore(allEventsSelector);
  const currentEvents = useStore(getCurrentEvents);
  const ownLocation = useStore(ownLocationSelector);
  const options = useStore(getOptions);
  const selectedStarters = useStore(getVisibleSelectedStarters);
  const selectedPois = useStore(getVisibleSelectedPois);
  const selectedTracks = useStore(getVisibleSelectedTracks);
  const selectedIds = useStore((state) => getSelectedIds(state.timer));
  const hiddenIds = useStore((state) => getHiddenIds(state.timer));
  const highlightedId = useStore(highlightedIdSelector);
  const highlightedLocation = useStore(highlightedLocationSelector);
  const starters = getEventStarters(currentEvents);
  const pois = getVisibleEventPois(currentEvents, hiddenIds);
  const tracks = getVisibleEventTracks(currentEvents, hiddenIds);
  const externalLayers = getEventExternalLayers(currentEvents);
  const starterTrackInfo = useStore(starterTrackInfoSelector);
  const time = useStore(timeSelector);
  const tailTimeout = currentEvents.reduce((r, a) => Math.max(r, a.options.tailTimeout), -1);

  const renderInfoBuffer = useStore(getRenderInfoBuffer);
  const trackLayerProps = useMemo(
    () => buildTrackLayerProps(tracks, selectedIds, highlightedId),
    [tracks, selectedIds, highlightedId],
  );
  // Memoized: a fresh accessor per render would rebuild and re-upload the marker GeoJSON on
  // every timer tick.
  const markerLines = useMemo(() => markerLinesByTrack(currentEvents), [currentEvents]);
  // Hovering a track shows its own markers alone. Only drawn tracks count: hovering a hidden one
  // has no markers to focus, and would otherwise blank every other track's.
  const focusedTrackId = focusedMarkerTrackId(
    trackLayerProps.filter((track) => markerLines.has(track.id)).map((track) => track.id),
    highlightedId,
  );
  const mapStyle = useMapStyle(options.mapType, externalLayers);

  const isReady = mapStyle != null;

  function handleMarkerClick(starter: StarterType) {
    toggleSelectedStarter(starter.id);
  }

  /**
   * Reapplies the track draw order. Mounting gets it right on its own, but a selection change only
   * reorders the React children — mapbox keeps its insertion order until the layers are moved.
   */
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (map == null || !mapIsLoaded) return;

    let retry = 0;
    const applyDrawOrder = () => {
      // Every mapbox call below throws while a style update is in flight, so come back next
      // frame. Not on `idle`: the player animates, so the map is never idle.
      if (!map.isStyleLoaded()) {
        // Cancel first: mapbox fires styledata repeatedly while a style loads, and without this
        // each one starts its own frame chain that outlives the effect's cleanup.
        cancelAnimationFrame(retry);
        retry = requestAnimationFrame(applyDrawOrder);
        return;
      }

      const layerIds = map.getStyle()?.layers.map((layer) => layer.id) ?? [];
      // Lines first, then their markers in the same order, so markers stay above every track and
      // a raised track raises its own. Zoom bands vary by length, so take what the style holds.
      const wanted = [
        ...trackLayerProps.map((track) => `layer-line-${track.id}`),
        ...trackLayerProps.flatMap((track) =>
          layerIds.filter((layerId) => layerId.startsWith(distanceMarkerLayerPrefix(track.id))),
        ),
      ].filter((layerId) => map.getLayer(layerId) != null);

      if (!needsReorder(layerIds, wanted)) return;

      // moveLayer without a before id appends, so walking the order in sequence rebuilds it.
      for (const layerId of wanted) map.moveLayer(layerId);
    };

    applyDrawOrder();
    // The layers are re-added after a basemap switch, in mount order rather than draw order.
    map.on('styledata', applyDrawOrder);
    return () => {
      cancelAnimationFrame(retry);
      map.off('styledata', applyDrawOrder);
    };
  }, [trackLayerProps, markerLines, mapIsLoaded, mapStyle]);

  /**
   * Publishes the camera for the share dialog to read. Takes the **visible** viewport, not the
   * sidebar-translated one — sharing the translated camera would bake this viewer's sidebar width
   * into the link and shift the framing for everyone who opens it.
   */
  function recordCurrentViewport(visibleViewport: {
    longitude: number;
    latitude: number;
    zoom: number;
  }) {
    setCurrentViewport({
      longitude: visibleViewport.longitude,
      latitude: visibleViewport.latitude,
      zoom: normaliseZoom(visibleViewport.zoom, visibleMapBounds.width),
    });
  }

  function handleChangeViewState(evt: ViewStateChangeEvent) {
    currentViewStateRef.current = evt.viewState;
    const visibleViewport = translateViewport(
      { ...evt.viewState, width, height },
      mapBounds,
      visibleMapBounds,
    );

    if (
      (interactionStateRef.current.isDragging || interactionStateRef.current.isZoomingRotating) &&
      !interactionStateRef.current.isOurTransition &&
      !areSelectedInView(
        selectedStarters,
        selectedPois,
        selectedTracks,
        ownLocation,
        visibleViewport,
        renderInfoBuffer,
      )[0]
    ) {
      setShouldFollow(false);
    }
  }

  // Remove Mapbox logo
  useEffect(() => {
    const mapboxLogoEl = document.querySelector(
      '.mapboxgl-ctrl-bottom-right .mapboxgl-ctrl-attrib',
    ) as HTMLAnchorElement;
    if (mapboxLogoEl != null) {
      mapboxLogoEl.parentNode?.removeChild(mapboxLogoEl);
    }
  }, [mapIsLoaded]);

  // mapbox only re-measures on window resize, which fires before the debounced size reaches the
  // container — without this the canvas keeps its old size and no tiles load for the new area.
  useEffect(() => {
    mapRef.current?.resize();
  }, [width, height]);

  // Initialize viewport and reinitialize it when the map is resized
  useEffect(() => {
    const visibleCoordinates = pois
      .map((poi) => [poi.latlng.lng, poi.latlng.lat, 0] as Tuple3)
      .concat(tracks.flatMap((track) => track.coordinates));
    if (options.overrideViewport != null) {
      // A camera handed over by a shared link wins over both the event viewport and auto-fitting.
      // Bail out until the map has been measured: a zero width would send translateViewport's
      // projection to a garbage centre, and the centre it produces is what gets kept below.
      if (visibleMapBounds.width === 0) return;

      const sharedViewport: Viewport = {
        ...currentViewStateRef.current,
        longitude: options.overrideViewport.longitude,
        latitude: options.overrideViewport.latitude,
        zoom: denormaliseZoom(options.overrideViewport.zoom, visibleMapBounds.width),
        width: visibleMapBounds.width,
        height: visibleMapBounds.height,
        padding: currentViewStateRef.current.padding,
      };
      const newViewport = translateViewport(sharedViewport, visibleMapBounds, mapBounds);
      mapRef.current?.jumpTo({
        center: [newViewport.longitude, newViewport.latitude],
        zoom: newViewport.zoom,
        bearing: newViewport.bearing,
        pitch: newViewport.pitch,
      });
      // Also feeds `initialViewState` for the case where the map has not mounted yet.
      currentViewStateRef.current = newViewport;
      interactionStateRef.current = {
        isDragging: false,
        isZoomingRotating: false,
        isOurTransition: false,
      };
      recordCurrentViewport(sharedViewport);
    } else if (
      (currentEvents.length === 1 && currentEvents[0].options.viewport != null) ||
      (visibleCoordinates.length === 0 && currentEvents.some((ev) => ev.options.viewport != null))
    ) {
      // Apply pre-defined viewport
      const eventViewport = currentEvents.find((ev) => ev.options.viewport != null)?.options
        .viewport;

      if (eventViewport == null) return;

      let newViewport: Viewport = {
        ...eventViewport,
        zoom: Math.log2(visibleMapBounds.width / (1000 / 2 ** eventViewport.zoom)),
        width: visibleMapBounds.width,
        height: visibleMapBounds.height,
        padding: currentViewStateRef.current.padding,
      };
      if (options.overrideZoom != null) {
        newViewport.zoom = options.overrideZoom;
      }
      recordCurrentViewport(newViewport);
      newViewport = translateViewport(newViewport, visibleMapBounds, mapBounds);
      mapRef.current?.jumpTo(newViewport);
      currentViewStateRef.current = newViewport;
      interactionStateRef.current = {
        isDragging: false,
        isZoomingRotating: false,
        isOurTransition: false,
      };
    } else if (visibleCoordinates.length > 0) {
      // Center map around geo features initially
      const visibleViewport = translateViewport(
        {
          ...currentViewStateRef.current,
          width,
          height,
        },
        mapBounds,
        visibleMapBounds,
      );
      let newViewport = fitBounds(visibleCoordinates, visibleViewport);
      if (options.overrideZoom != null) {
        newViewport.zoom = options.overrideZoom;
      }
      recordCurrentViewport(newViewport);
      newViewport = translateViewport(newViewport, visibleMapBounds, mapBounds);
      mapRef.current?.jumpTo(newViewport);
      currentViewStateRef.current = newViewport;
      interactionStateRef.current = {
        isDragging: false,
        isZoomingRotating: false,
        isOurTransition: false,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, pois, tracks, currentEvents]);

  // Keep selected starters in view
  useEffect(() => {
    const currentViewState = currentViewStateRef.current;
    if (currentViewState != null) {
      const visibleViewport = translateViewport(
        { ...currentViewState, width, height },
        mapBounds,
        visibleMapBounds,
      );

      const [allInView, selectedCoordinates] = areSelectedInView(
        selectedStarters,
        selectedPois,
        selectedTracks,
        ownLocation,
        visibleViewport,
        renderInfoBuffer,
      );

      const isInteracting =
        !shouldFollow ||
        interactionStateRef.current.isDragging ||
        interactionStateRef.current.isOurTransition ||
        // Interrupting a running ease makes mapbox fire `moveend` synchronously.
        (mapRef.current?.isEasing() ?? false);

      if (
        !allInView &&
        !isInteracting &&
        visibleMapBounds.width > 100 &&
        visibleMapBounds.height > 100
      ) {
        if (selectedCoordinates.length === 1) {
          interactionStateRef.current.isOurTransition = true && mapIsLoaded;

          const translatedLngLat = translateViewport(
            {
              ...visibleViewport,
              longitude: selectedCoordinates[0][0],
              latitude: selectedCoordinates[0][1],
            },
            visibleMapBounds,
            mapBounds,
          );
          mapRef.current?.easeTo({
            center: [translatedLngLat.longitude, translatedLngLat.latitude],
            duration: FAST_ANIMATION_DURATION,
            easing: easeInOut,
          });
        } else if (selectedCoordinates.length > 1) {
          const fittedViewport = fitBounds(selectedCoordinates, visibleViewport);
          const translatedFittedViewport = translateViewport(
            { ...fittedViewport, zoom: Math.min(currentViewState.zoom, fittedViewport.zoom) },
            visibleMapBounds,
            mapBounds,
          );
          interactionStateRef.current.isOurTransition = true && mapIsLoaded;
          mapRef.current?.easeTo({
            center: [translatedFittedViewport.longitude, translatedFittedViewport.latitude],
            zoom: translatedFittedViewport.zoom,
            duration: FAST_ANIMATION_DURATION,
            easing: easeInOut,
          });
        }
      }
    }
  }, [
    mapIsLoaded,
    renderInfoBuffer,
    ownLocation,
    visibleMapBounds,
    mapBounds,
    selectedStarters,
    selectedPois,
    selectedTracks,
    shouldFollow,
  ]);

  const layers = [];
  if (deviceRef.current != null) {
    // Draw starter tails
    if (tailTimeout !== 0) {
      layers.push(
        ...starterTrackInfo.map((info) =>
          createStarterTrackLayer(
            info.tracks,
            starters,
            renderInfoBuffer,
            time,
            info.tailTimeout,
            info.markerTimeout,
          ),
        ),
      );
    }
    // Draw starter dots
    layers.push(
      createStarterParticleLayer({
        renderInfoBuffer,
        gpuDevice: deviceRef.current,
        onClick: (starterIndex) => handleMarkerClick(starters[starterIndex]),
        onHover: (starterIndex) => setHighlighted(starters[starterIndex]?.id ?? null),
      }),
    );
    // Draw text-based POI
    layers.push(
      ...createPOILayer({
        pois,
        ownLocation,
        selectedIds,
        gpuDevice: deviceRef.current,
        largeMode: options.largeMode,
        onClick: (poi) => toggleSelectedStarter(poi.id),
        onHover: (poi) => setHighlighted(poi?.id ?? null),
      }),
    );
    // Draw selected starter flags
    layers.push(
      ...createStarterMarkerLayer({
        renderInfoBuffer,
        starters,
        gpuDevice: deviceRef.current,
        selectedIds,
        hiddenIds,
        flagContentByEventIndex: allEvents.map((ev) => ev.options.flagContent),
        selectedFlagContentByEventIndex: allEvents.map((ev) => ev.options.selectedFlagContent),
        overrideFlagContent: options.overrideFlagContent,
        overrideSelectedFlagContent: options.overrideSelectedFlagContent,
        largeMode: options.largeMode,
        onClick: (starter) => handleMarkerClick(starter),
        onHover: (starter) => setHighlighted(starter?.id ?? null),
      }),
    );

    layers.push(createTrackHoverLayer(highlightedLocation));
  }

  return (
    <div className="map">
      <div style={{ position: 'absolute', top: 0, left: 0 }}>
        {isReady && (
          <ReactMap
            initialViewState={currentViewStateRef.current}
            key="static-map"
            mapStyle={mapStyle}
            mapboxAccessToken={import.meta.env.VITE_FRONTEND_MAPBOX_ACCESS_TOKEN}
            boxZoom={false}
            reuseMaps={true}
            RTLTextPlugin="https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js"
            ref={mapRef}
            onLoad={() => {
              setMapIsLoaded(true);
            }}
            onDragStart={() => {
              interactionStateRef.current.isDragging = true;
            }}
            onDragEnd={() => {
              interactionStateRef.current.isDragging = false;
            }}
            onZoomStart={() => {
              interactionStateRef.current.isZoomingRotating = true;
            }}
            onZoomEnd={() => {
              interactionStateRef.current.isZoomingRotating = false;
            }}
            onRotateStart={() => {
              interactionStateRef.current.isZoomingRotating = true;
            }}
            onRotateEnd={() => {
              interactionStateRef.current.isZoomingRotating = false;
            }}
            onMove={handleChangeViewState}
            onMoveEnd={(evt: ViewStateChangeEvent) => {
              interactionStateRef.current.isOurTransition = false;
              handleChangeViewState(evt);
              // Once per gesture, not per frame — onMove would fire this continuously.
              recordCurrentViewport(
                translateViewport({ ...evt.viewState, width, height }, mapBounds, visibleMapBounds),
              );
            }}
            style={{ width, height }}
          >
            {trackLayerProps.map((track) => {
              const markerLine = markerLines.get(track.id);
              return (
                <TrackLayer
                  key={track.id}
                  id={track.id}
                  tracks={[track.coordinates]}
                  color={track.color}
                  lineWidth={track.lineWidth}
                  distanceMarkers={
                    markerLine == null
                      ? undefined
                      : {
                          line: markerLine,
                          metersPerIndex: SHADOW_TRACK_STEP,
                          unitType: options.unitType,
                          largeMode: options.largeMode,
                          visible: focusedTrackId == null || focusedTrackId === track.id,
                        }
                  }
                />
              );
            })}
            <DeckGLOverlay
              layers={layers}
              onDeviceInitialized={(device) => {
                deviceRef.current = device;
              }}
            />
            <div key="map-ctrl-container" className="map-ctrl-container">
              {!options.mapOnly && (
                <>
                  {(selectedStarters.length > 0 ||
                    selectedPois.length > 0 ||
                    selectedTracks.length > 0 ||
                    ownLocation != null) &&
                    !shouldFollow && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          right: 41,
                          zIndex: 10,
                        }}
                      >
                        <div className="mapboxgl-ctrl-group">
                          <button
                            className="mapboxgl-ctrl-icon follow-selected-button"
                            title={t('map.followSelected')}
                            {...onClickGenerator(() => setShouldFollow(true))}
                          >
                            <IconThumbtack /> {t('map.followSelected')}
                          </button>
                        </div>
                      </div>
                    )}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      zIndex: 10,
                    }}
                  >
                    <NavigationControl />
                    <div style={{ height: 10 }} />
                    <OwnLocationControl />
                    {options.fullscreenButton && (
                      <>
                        <div style={{ height: 10 }} />
                        <FullscreenControl />
                      </>
                    )}
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      top: visibleMapBounds.height - 50,
                      right: 0,
                      zIndex: 10,
                    }}
                  >
                    <ScaleControl
                      style={{ bottom: 0, right: 0 }}
                      unit={options.unitType.toLowerCase() as 'metric' | 'imperial' | 'nautical'}
                    />
                  </div>
                </>
              )}
            </div>
          </ReactMap>
        )}
      </div>
    </div>
  );
}

export default memo(MapComponent);
